// eslint-disable-next-line css-modules/no-unused-class
import { FC, useEffect, useState } from 'react';
import classNames from 'classnames';
import { translate } from 'translations';
import { Avatar, Icon, Divider, Button } from 'learning-panel-components';
import moment from 'moment';
import { getPlatformUrl } from 'utils';
import { tournamentServer } from 'network-utils';
import EventCountdown from './eventCountDown';
import styles from './tournamentBanner.module.scss';
import EventDate from './eventDate';

const TournamentBanner: FC<TournamentCardProps> = (props: TournamentCardProps) => {
    const {
        name,
        frameworks,
        status,
        numberOfRounds,
        participants,
        numberOfUsers,
        numJoinedUsers,
        endDate,
        dateTime,
        actualStartTime,
        uuid,
        locale
    } = props;

    const [isFarFuture, setIsFarFuture] = useState<boolean>(false);
    const [scheduledTimeIsUp, setScheduledTimeIsUp] = useState<boolean>(true);
    const [alreadyStarted, setAlreadyStarted] = useState<boolean>(true);
    const limitedParticipants = participants?.slice(0, 3);
    const joinedUsersNoAvatar = Number(numJoinedUsers) - Number(limitedParticipants?.length || 0);

    const getCategories = () => {
        const categoriesArray = frameworks.map((subtitle, index) => {
            return `#${subtitle}`;
        });
        return categoriesArray.join(', ');
    };

    const calcTournamentUrl = () => {
        return `${getPlatformUrl()}/tournaments/registration/${uuid}`;
    };

    const isActive = ['opened', 'inProgress', 'pending'].includes(status);

    const tagsByStatus: { [key: string]: string } = {
        pending: translate('Upcoming!'),
        opened: translate('OpeningSoon!'),
        inProgress: translate('Active')
    };

    const tag = tagsByStatus[status] || translate('OccurredInThePast');

    useEffect(() => {
        const twoDaysFromNow = new Date();
        twoDaysFromNow.setDate(twoDaysFromNow.getDate() + 2);

        if (status === 'pending') {
            const dateTimeMoment = moment(dateTime);
            if (dateTimeMoment.isAfter(twoDaysFromNow)) {
                setIsFarFuture(true);
            }
            if (dateTimeMoment.isAfter(new Date())) {
                setScheduledTimeIsUp(false);
            }
        }

        if (status === 'inProgress' && actualStartTime && moment(actualStartTime).isAfter(new Date())) {
            setAlreadyStarted(false);
        }
    }, [dateTime, status, actualStartTime]);

    useEffect(() => {
        let timeout: any = null;
        if (isFarFuture) {
            const twoDaysFromNow = new Date();
            twoDaysFromNow.setDate(twoDaysFromNow.getDate() + 2);
            const changeTime = new Date(dateTime).getTime() - twoDaysFromNow.getTime();
            timeout = setTimeout(() => {
                setIsFarFuture(false);
            }, changeTime);
        }

        return () => {
            if (isFarFuture) {
                clearTimeout(timeout);
            }
        };
    }, [dateTime, isFarFuture]);

    useEffect(() => {
        let timeout: any = null;
        if (!scheduledTimeIsUp) {
            const changeTime = new Date(dateTime).getTime() - new Date().getTime();
            timeout = setTimeout(() => {
                setScheduledTimeIsUp(true);
            }, changeTime);
        }

        return () => {
            if (!scheduledTimeIsUp) {
                clearTimeout(timeout);
            }
        };
    }, [dateTime, scheduledTimeIsUp]);

    useEffect(() => {
        let timeout: any = null;
        if (actualStartTime && !alreadyStarted) {
            const changeTime = new Date(actualStartTime).getTime() - new Date().getTime();
            timeout = setTimeout(() => {
                setAlreadyStarted(true);
            }, changeTime);
        }

        return () => {
            if (!alreadyStarted) {
                clearTimeout(timeout);
            }
        };
    }, [actualStartTime, alreadyStarted]);

    return (
        <div>
            <div className={isActive ? styles['tournament-banner-wrapper'] : styles['tournament-past-banner-wrapper']}>
                <div className={styles['inner-wrapper']}>
                    <div className={styles.tag}>{tag}</div>
                    <div
                        className={classNames(
                            styles['tournament-left-wrapper'],
                            !isActive && styles['past-tournament']
                        )}
                    >
                        <div className={styles['tournament-title-wrapper']}>
                            <Icon name="tournamentBannerIcon" style={{ marginTop: '0.3rem' }} />
                            <div className={styles['tournament-title']}>{translate('TOURNAMENT')}</div>
                        </div>
                        <div className={styles['tournament-name']}>{name}</div>
                        <div className={styles['tournament-categories']}>
                            <div className={styles.category} title={getCategories()}>
                                {getCategories()}
                            </div>
                        </div>

                        {(status === 'opened' || status === 'inProgress') && (
                            <div className={styles['button-joined-wrapper']}>
                                <div style={{ width: '13.875rem', height: '3.3rem' }}>
                                    <a href={calcTournamentUrl()} target="_blank" rel="noreferrer">
                                        <Button
                                            lowerCase
                                            id="user-details-submit-button"
                                            type={'gradient'}
                                            className={styles.button}
                                            text={translate('JoinTournament')}
                                        />
                                    </a>
                                </div>
                                <div className={styles['joined-participants']}>
                                    {participants && participants.length > 0 ? translate('JustJoined') : ''}
                                </div>
                                <div className={styles['row-name']}>
                                    {limitedParticipants &&
                                        limitedParticipants.map(user => (
                                            <div className={styles.avatar} key={user.profilePicture}>
                                                <Avatar
                                                    key={user.profilePicture}
                                                    name={user.nickname}
                                                    avatarSrc={`${process.env.PUBLIC_URL}/images/avatars/tournaments/${user.profilePicture}.svg`}
                                                    tooltipText={user.nickname}
                                                    tooltipOnHover={true}
                                                ></Avatar>
                                            </div>
                                        ))}
                                </div>
                                <div className={styles.participants}>
                                    {joinedUsersNoAvatar > 0 ? `+${joinedUsersNoAvatar}` : ''}
                                </div>
                            </div>
                        )}
                        {status === 'pending' && <EventDate dateTime={new Date(dateTime)} locale={locale} />}
                    </div>
                    <Divider
                        orientation="vertical"
                        color="rgba(255, 255, 255, 0.2)"
                        className={styles['long-divider']}
                    />
                    <div className={styles['tournament-right-wrapper']}>
                        <div className={styles['tournament-box-wrapper']}>
                            {status === 'inProgress' && (
                                <>
                                    <div className={styles['tournament-box-title']}>
                                        {translate(alreadyStarted ? 'AlreadyRunning' : 'WaitForIt')}
                                    </div>
                                    {alreadyStarted && <EventCountdown value={actualStartTime as string} countUp />}
                                </>
                            )}
                            {status === 'opened' && (
                                <>
                                    <div className={styles['tournament-box-title']}>{translate('WaitForIt')}</div>
                                </>
                            )}
                            {status === 'pending' && (
                                <>
                                    {!isFarFuture && !scheduledTimeIsUp ? (
                                        <>
                                            <div className={styles['tournament-box-title']}>
                                                {translate('StartsIn')}
                                            </div>
                                            <EventCountdown value={dateTime} />
                                        </>
                                    ) : (
                                        <div className={styles['tournament-box-title']}>{translate('WaitForIt')}</div>
                                    )}
                                </>
                            )}
                            {status === 'finished' && (
                                <div className={styles['end-date']}>
                                    <div className={styles['tournament-box-title']}>{translate('ENDED')}</div>
                                    <div className={styles['tournament-box-title']}>
                                        {moment(endDate).format('D MMMM YYYY')}
                                    </div>
                                </div>
                            )}

                            <Divider
                                orientation="horizontal"
                                color="rgba(255, 255, 255, 0.2)"
                                className={styles['short-divider']}
                            />
                            <div className={styles['details-wrapper']}>
                                <div className={styles.details}>
                                    <Icon
                                        name="rounds"
                                        className={styles['type-icon']}
                                        tooltipText={translate('NumberOfRounds')}
                                    />
                                    <div className={styles.text}>{numberOfRounds}</div>
                                </div>
                                <Divider
                                    orientation="vertical"
                                    className={styles.divider}
                                    color="rgba(255, 255, 255, 0.5)"
                                />
                                <div className={styles.details}>
                                    <Icon
                                        name="people"
                                        className={styles['type-icon']}
                                        style={{ marginTop: '0.2rem' }}
                                        tooltipText={translate('InvitedUsers')}
                                    />
                                    <div className={styles.text}>{numberOfUsers}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

interface TournamentCardProps {
    name: string;
    frameworks: string[];
    status: string;
    numberOfRounds: number;
    numberOfUsers: number;
    dateTime: string;
    actualStartTime?: string | null;
    endDate?: Date;
    participants?: tournamentServer.UserData[];
    numJoinedUsers: number;
    canJoin: boolean;
    canManage: boolean;
    uuid?: string;
    locale: string;
}

export default TournamentBanner;
